import {
  AssignmentOutlined,
  Check,
  CloseOutlined,
  HandshakeOutlined,
  QuestionMarkOutlined,
  TrackChangesOutlined,
} from '@mui/icons-material';
import { Box, Button, IconButton, Tooltip, Typography } from '@mui/material';
import React from 'react';
import { useTranslation } from 'react-i18next';
import { useNavigate } from 'react-router-dom';
import { useGhgOrganization } from 'src/common/hooks';
import {
  useCommitmentTarget,
  useCreateSupplierSurveyOwn,
  useRabenConsent,
  useSupplierSurveyOwn,
  useYearScope12Completion,
} from 'src/Ghg/hooks';
import DirectEmissionIcon from 'src/images/svg/directEmissionIcon';
import ElectricityEmissionsIcon from 'src/images/svg/electricityEmissionsIcon';
import { useAppSelector } from 'src/redux-file/hooks';
import themeColors from 'src/theme/colors';
import { OrganizationTag } from 'src/utils/tags';
import DownloadCertificateItem from '../OrganizationSidebar/DownloadCertificateItem';
import VerifiedIcon from '@mui/icons-material/Verified';
import { toast } from 'react-toastify';
import { SUPPLIER_COMMITMENT_TAB_PATH } from '../TargetPromptModal';

export default function Clippy() {
  const { t } = useTranslation();
  const navigate = useNavigate();
  const organization = useGhgOrganization();
  const storageKey = `ghg_clippy_open_${organization.id}`;

  const [open, setOpen] = React.useState<boolean>(() => {
    try {
      const raw = localStorage.getItem(storageKey);
      if (raw === null) return true;
      return raw === '1';
    } catch {
      return true;
    }
  });

  React.useEffect(() => {
    try {
      localStorage.setItem(storageKey, open ? '1' : '0');
    } catch {
      // ignore storage errors (e.g. privacy mode)
    }
  }, [open, storageKey]);
  const { selectedDate } = useAppSelector((state) => state.questionnaire);
  const selectedYear = String(selectedDate.getFullYear());
  const { data } = useSupplierSurveyOwn(organization.id, selectedYear);
  const createOwnSurvey = useCreateSupplierSurveyOwn(
    organization.id,
    selectedYear
  );

  // RG 28.08 2.3 / Raben 14.10: the Scope 1/2 steps and the certificate step
  // share one rule with the sidebar certificate and the readiness checklist
  // (every period of the year; Scope 1 also needs data).
  const { scope1Complete, scope2Complete } = useYearScope12Completion(
    organization,
    selectedDate.getFullYear()
  );
  const isRaben = organization.tags.includes(OrganizationTag.Raben);

  // C18 Goal 1/BR-2: the two new steps below (Commitment, Target) read C15's
  // consent endpoint and C17's target endpoint directly -- both hooks
  // already exist, this is the first widget consumer of either. Called
  // unconditionally (Rules of Hooks) even though the early `return null`
  // below skips rendering for non-raben orgs entirely.
  const { data: consentData } = useRabenConsent(organization.id);
  // Client-reported 2026-08-27: this unconditional GET was the source of
  // the raw "must be accepted before setting a target" 409 toast on every
  // not-yet-accepted carrier's dashboard -- the endpoint refuses before an
  // accept, so only ask once consent is actually accepted (same gate
  // TargetPromptModal uses).
  const { data: targetData } = useCommitmentTarget(organization.id, {
    enabled: consentData?.consent === true,
  });

  if (!isRaben) {
    return null;
  }

  // RG live demo 2026-10-02: the two commitment steps follow the Letter
  // window the client sets per branch (Settings -> "Okna Listu Zobowiązania
  // Przewoźnika"), not the old raben_commitment_pilot tag, which the client
  // has no way to set. A carrier that has already decided keeps the steps
  // after its window closes. The existing four steps stay ungated.
  const commitmentStepsVisible =
    !!consentData?.window_active ||
    consentData?.consent === true ||
    consentData?.consent === false;
  const commitmentAccepted = consentData?.consent === true;
  const commitmentDeclined = consentData?.consent === false;
  const targetSet = !!targetData?.target;
  const goToSupplierCommitmentTab = () =>
    navigate(SUPPLIER_COMMITMENT_TAB_PATH);

  const supplierSurveyOwnLink = data?.access_token
    ? `/suppliers/${data.access_token}/main-page?vat_identifier=${organization.vat_identifier}`
    : null;

  const answer = data?.answer;
  const wantsPlatform = answer?.wants_to_use_platform;

  const esgFinished =
    (answer?.has_sedex_questionnaire === true &&
      !!answer?.zc_code &&
      !!answer?.saq_file) ||
    (answer?.has_sedex_questionnaire === false &&
      !!answer?.questionnaire_answer_esg_submitted);

  const envFinished = !!answer?.questionnaire_answer_environment_submitted;

  // Scope 1/2 should be available only after deciding to use platform.
  const scopeLocked = wantsPlatform !== true;

  // If supplier completes carbon footprint without platform (for this year),
  // mark both scopes as completed for Clippy.
  const scopeCompletedBecauseOffPlatform =
    wantsPlatform === false && envFinished === true;

  const scope1Finished =
    scopeCompletedBecauseOffPlatform ||
    (wantsPlatform === true && scope1Complete);
  const scope2Finished =
    scopeCompletedBecauseOffPlatform ||
    (wantsPlatform === true && scope2Complete);

  const surveyFinished =
    !!answer?.is_submitted || (wantsPlatform === true && esgFinished === true);

  // Certificate is only available after the supplier survey is fully submitted
  // AND platform scope 1 & 2 data is completed (the same rule as the sidebar).
  const certAvailable =
    !!answer?.is_submitted && scope1Complete && scope2Complete;

  const scopeLockedTooltip = t('ghg.clippy.scope12LockedTooltip', {
    year: selectedYear,
  });

  return (
    <>
      {open && (
        <Box
          position="fixed"
          bottom="18px"
          right="18px"
          zIndex={101}
          bgcolor={themeColors.pureWhite}
          borderRadius="24px"
          border="1px solid"
          borderColor={themeColors.ghgMain}
        >
          <IconButton
            sx={{
              position: 'absolute',
              top: '10px',
              right: '10px',
              color: themeColors.ghgMain,
            }}
            onClick={() => setOpen(false)}
          >
            <CloseOutlined />
          </IconButton>
          <Box display="flex" flexDirection="column" m="12px">
            <Typography sx={{ mr: '50px', mb: '8px' }} variant="h1">
              {t('ghg.clippy.title')}
            </Typography>
            <Typography variant="h5">{t('ghg.clippy.description')}</Typography>
            {/* C18 Goal 1: Commitment step, ordered FIRST. EC-1: a decline
                shows a distinct label instead of the default one -- it
                still isn't a "done" (Check) state, since declining isn't
                completing the step. */}
            {commitmentStepsVisible && (
              <Button
                onClick={goToSupplierCommitmentTab}
                startIcon={<HandshakeOutlined />}
                endIcon={commitmentAccepted ? <Check /> : undefined}
                sx={{
                  mt: '8px',
                  justifyContent: 'start',
                  bgcolor: commitmentAccepted
                    ? themeColors.disabledLabel
                    : undefined,
                  ':hover': {
                    bgcolor: commitmentAccepted
                      ? themeColors.ghgMain
                      : undefined,
                  },
                }}
              >
                <Typography>
                  {t(
                    commitmentDeclined
                      ? 'ghg.clippy.commitmentDeclined'
                      : 'ghg.clippy.commitmentStep'
                  )}
                </Typography>
              </Button>
            )}
            <Button
              onClick={() => {
                if (supplierSurveyOwnLink) {
                  window.open(supplierSurveyOwnLink, '_blank');
                  return;
                }
                createOwnSurvey.mutate(undefined, {
                  onSuccess: (created) => {
                    const link = created?.access_token
                      ? `/suppliers/${created.access_token}/main-page?vat_identifier=${organization.vat_identifier}`
                      : null;
                    if (!link) {
                      toast.error(String(t('ghg.supplierSurvey.submitError')));
                      return;
                    }
                    // Newly created surveys don't have an answer yet, so start from initial questions.
                    const initialLink = created?.access_token
                      ? `/suppliers/${created.access_token}/initial-questions?vat_identifier=${organization.vat_identifier}`
                      : null;
                    window.open(initialLink || link, '_blank');
                  },
                  onError: () => {
                    toast.error(String(t('ghg.supplierSurvey.submitError')));
                  },
                });
              }}
              startIcon={<AssignmentOutlined />}
              endIcon={surveyFinished ? <Check /> : undefined}
              disabled={createOwnSurvey.isLoading}
              sx={{
                mt: '8px',
                justifyContent: 'start',
                bgcolor: surveyFinished ? themeColors.disabledLabel : undefined,
                ':hover': {
                  bgcolor: surveyFinished ? themeColors.ghgMain : undefined,
                },
              }}
            >
              <Typography>
                {t('ghg.clippy.supplierSurveyOwn', { year: selectedYear })}
              </Typography>
            </Button>
            <Tooltip
              title={scopeLockedTooltip}
              disableHoverListener={!scopeLocked}
              placement="left"
            >
              <Box sx={{ width: '100%' }}>
                <Button
                  onClick={() =>
                    navigate(
                      '/ghg/emissions/questionnairepage-direct-emissions'
                    )
                  }
                  startIcon={<DirectEmissionIcon />}
                  endIcon={scope1Finished ? <Check /> : undefined}
                  disabled={scopeLocked}
                  sx={{
                    mt: '8px',
                    justifyContent: 'start',
                    bgcolor: scope1Finished
                      ? themeColors.disabledLabel
                      : undefined,
                    '&.Mui-disabled': scope1Finished
                      ? {
                          bgcolor: themeColors.disabledLabel,
                          color: themeColors.ghgMain,
                        }
                      : undefined,
                    ':hover': {
                      bgcolor: scope1Finished ? themeColors.ghgMain : undefined,
                    },
                  }}
                >
                  <Typography>{t('direct-emission.title')}</Typography>
                </Button>
              </Box>
            </Tooltip>

            <Tooltip
              title={scopeLockedTooltip}
              disableHoverListener={!scopeLocked}
              placement="left"
            >
              <Box sx={{ width: '100%' }}>
                <Button
                  onClick={() =>
                    navigate(
                      '/ghg/emissions/questionnairepage-electricity-emissions'
                    )
                  }
                  startIcon={<ElectricityEmissionsIcon />}
                  endIcon={scope2Finished ? <Check /> : undefined}
                  disabled={scopeLocked}
                  sx={{
                    mt: '8px',
                    justifyContent: 'start',
                    bgcolor: scope2Finished
                      ? themeColors.disabledLabel
                      : undefined,
                    '&.Mui-disabled': scope2Finished
                      ? {
                          bgcolor: themeColors.disabledLabel,
                          color: themeColors.ghgMain,
                        }
                      : undefined,
                    ':hover': {
                      bgcolor: scope2Finished ? themeColors.ghgMain : undefined,
                    },
                  }}
                >
                  <Typography>{t('electricity-emission.title')}</Typography>
                </Button>
              </Box>
            </Tooltip>
            {/* C18 Goal 1/EC-1: Target step, before the certificate --
                hidden entirely when the commitment was declined (setting a
                target with no accepted commitment makes no sense, and the
                backend itself 409s that path -- C17 EC-2). */}
            {commitmentStepsVisible && !commitmentDeclined && (
              <Button
                onClick={goToSupplierCommitmentTab}
                startIcon={<TrackChangesOutlined />}
                endIcon={targetSet ? <Check /> : undefined}
                sx={{
                  mt: '8px',
                  justifyContent: 'start',
                  bgcolor: targetSet ? themeColors.disabledLabel : undefined,
                  ':hover': {
                    bgcolor: targetSet ? themeColors.ghgMain : undefined,
                  },
                }}
              >
                {/* RG 28.08 8.4: a refused target (no target set) gets its
                    own label; like the declined Letter it is not a "done"
                    state, and the click still leads to the tab. */}
                <Typography>
                  {t(
                    !targetSet && targetData?.declined
                      ? 'ghg.clippy.targetDeclined'
                      : 'ghg.clippy.targetStep'
                  )}
                </Typography>
              </Button>
            )}
            <DownloadCertificateItem
              disabled={!certAvailable}
              button={
                <Button
                  disabled={!certAvailable}
                  startIcon={<VerifiedIcon />}
                  sx={{
                    mt: '8px',
                    justifyContent: 'start',
                  }}
                >
                  <Typography>{t('menu.download-certificate')}</Typography>
                </Button>
              }
            />
          </Box>
        </Box>
      )}
      <IconButton
        sx={{
          position: 'fixed',
          bottom: '18px',
          right: '18px',
          zIndex: 100,
          bgcolor: themeColors.pureWhite,
          borderRadius: '50%',
          border: '6px solid',
          borderColor: themeColors.ghgMain,
          color: themeColors.ghgMain,
          ':hover': {
            bgcolor: themeColors.ghgMain,
            color: themeColors.pureWhite,
          },
        }}
        onClick={() => setOpen(true)}
      >
        <QuestionMarkOutlined
          sx={{
            width: '50px',
            height: '50px',
          }}
        />
      </IconButton>
    </>
  );
}
